import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion, useMotionValue, useTransform } from 'framer-motion'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { debounce, isEmpty, isEqual } from 'lodash'
import { Button, Pill } from '@demandlocal/components'
import { ReactComponent as FilterIcon } from '../../images/icons/filters_icon.svg'
import { InPortal, HtmlPortalNode } from 'react-reverse-portal'
import { usePrevious } from '../../lib/hooks/usePrevious'
import {
  arrow,
  autoUpdate,
  FloatingArrow,
  useFloating,
  shift,
  offset,
} from '@floating-ui/react'
import { match, P } from 'ts-pattern'

const FiltersContext = ({
  setFilters,
  portalNode,
  defaultValues,
  children,
  showFilters = false,
}: {
  setFilters: React.SetStateAction<any>
  portalNode: HtmlPortalNode
  defaultValues?: Record<string, any>
  children: React.ReactNode
  showFilters?: boolean
}): JSX.Element => {
  const [expanded, setExpanded] = useState(false)

  const formContext = useForm({
    mode: 'onChange',
    resetOptions: { keepDefaultValues: true },
    defaultValues: useMemo(() => defaultValues, [defaultValues]),
  })

  const isAnimating = useMotionValue(false)
  const containerOverflow = useTransform(isAnimating, (isAnimating) =>
    isAnimating ? 'overflow-hidden' : 'overflow-visible',
  )

  const debouncedSetColumnFilters = debounce(setFilters, 300)

  const formValues = useWatch({ control: formContext.control })
  const prevFormValues = usePrevious(formValues)

  const isFiltered: boolean = useMemo(
    () => Object.values(formValues).reduce((acc, cur) => acc || !isEmpty(cur), false),
    [formValues],
  )

  const filtersCount: number = useMemo(
    () =>
      Object.values(formValues).reduce((acc, cur) => acc + (!isEmpty(cur) ? 1 : 0), 0),
    [formValues],
  )

  useEffect(() => {
    // if ctrl+f is pressed, open/close filters
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'f' && e.ctrlKey) {
        e.preventDefault()
        setExpanded((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    if (!isEqual(prevFormValues, formValues)) debouncedSetColumnFilters(formValues)
  }, [formValues, prevFormValues, debouncedSetColumnFilters])

  useEffect(() => {
    if (!isEmpty(defaultValues)) {
      setTimeout(() => {
        setExpanded(true)
      }, 300)
    }
  }, [defaultValues])

  const resetValues = useCallback(() => {
    const newValues = Object.entries(formValues).reduce(
      (acc: Record<string, unknown>, [key, value]) => {
        if (!isEmpty(value)) {
          if (typeof value !== 'object') acc[key] = ''
          if (typeof value === 'object') acc[key] = null
        }
        return acc
      },
      {},
    )
    formContext.reset(newValues)
  }, [formContext, formValues])
  const arrowRef = useRef(null)
  const { context, strategy, refs } = useFloating({
    placement: 'bottom-end',
    middleware: [
      shift(),
      arrow({ element: arrowRef }),
      offset({ mainAxis: 0, crossAxis: 0 }),
    ],
    open: expanded,
    whileElementsMounted: autoUpdate,
    onOpenChange: setExpanded,
  })

  return (
    <div>
      <InPortal node={portalNode}>
        <div className={'flex flex-row gap-3'}>
          {expanded && !showFilters && (
            <div
              ref={refs.setFloating}
              className={'relative left-1/2 flex w-fit h-fit z-10'}>
              <FloatingArrow
                width={24}
                height={12}
                className={`fill-primary-persian-blue/5 stroke-primary-persian-blue top-[32px] `}
                ref={arrowRef}
                context={context}
              />
            </div>
          )}
          <AnimatePresence presenceAffectsLayout={true} mode={'wait'}>
            {isFiltered && !showFilters && (
              <motion.div
                className={'flex-row flex gap-4'}
                key={'filters-buttons'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  type: 'spring',
                  mass: 1,
                  damping: 50,
                  stiffness: 500,
                }}>
                <Button
                  animate={false}
                  variant={'tertiary'}
                  onClick={(e) => {
                    e.preventDefault()
                    resetValues()
                  }}>
                  <span className={'text-primary-xiketic'}>Clear Filters</span>
                </Button>
              </motion.div>
            )}
            {showFilters && (
              <motion.div
                className={'flex-row flex gap-4 flex-wrap items-center justify-end'}
                key={'filters-buttons'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  type: 'spring',
                  mass: 1,
                  damping: 50,
                  stiffness: 500,
                }}>
                {Object.entries(formValues).map(([key, value]) => {
                  if (isEmpty(value)) return null
                  const isMultiSelectOption = Array.isArray(value)
                  const parsedValue = match(value)
                    .with(P.array(P.any), () =>
                      value.map((v) => ({ label: v.label, value: v.value })),
                    )
                    .with({ label: P.string, value: P.string }, (matchedValue) => [
                      matchedValue,
                    ])
                    .otherwise(() => [{ label: value, value: value }])
                  return parsedValue.map((v) => {
                    return (
                      <Pill
                        key={key}
                        type={'blue'}
                        variant="squared"
                        onDismiss={() => {
                          if (isMultiSelectOption) {
                            formContext.setValue(
                              key,
                              value.filter((_value) => v.value !== _value.value),
                            )
                          } else {
                            formContext.setValue(key, '')
                          }
                        }}>
                        <span>{key.split('_').join(' ')}:</span>
                        <span>{v.label}</span>
                      </Pill>
                    )
                  })
                })}
              </motion.div>
            )}
          </AnimatePresence>
          <div ref={refs.setReference}>
            <Button
              animate={false}
              key={'filters'}
              variant={isFiltered ? 'filtered' : 'tertiary'}
              rightSlot={
                <motion.div className={'flex items-center justify-center gap-2'}>
                  {filtersCount > 0 && (
                    <motion.div
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      key={'filters-count'}
                      className={
                        'h-5 bg-primary-persian-blue text-center flex items-center text-white p-2 rounded-full text-xs'
                      }>
                      {filtersCount}
                    </motion.div>
                  )}
                  <FilterIcon
                    className={
                      isFiltered ? 'fill-primary-persian-blue' : 'fill-primary-xiketic'
                    }
                  />
                </motion.div>
              }
              onClick={() => setExpanded((prev) => !prev)}>
              <span className={'text-primary-xiketic'}>Filters</span>
            </Button>
          </div>
        </div>
      </InPortal>

      <FormProvider {...formContext}>
        <AnimatePresence>
          {expanded && (
            <motion.div
              key={'filters-form'}
              animate={'open'}
              initial={'collapsed'}
              exit={'collapsed'}
              onAnimationStart={() => {
                isAnimating.set(true)
              }}
              onAnimationComplete={() => {
                isAnimating.set(false)
              }}
              variants={{
                open: {
                  opacity: 1,
                  height: 'auto',
                },
                collapsed: {
                  opacity: 0,
                  height: 0,
                },
              }}
              className={`${containerOverflow}`}
              transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}>
              <motion.div
                className={
                  'p-10 grid grid-cols-2 lg:grid-cols-3 gap-5 mb-9  bg-primary-persian-blue/5 border-2 rounded-lg border-primary-persian-blue/10'
                }>
                {children}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </FormProvider>
    </div>
  )
}

export default FiltersContext
